import React, { ComponentProps, FC, useCallback } from 'react';

import Banner from '@adsk/alloy-react-banner';
import { Flex, Text } from '@adsk/alloy-react-box';
import Dropzone from '@adsk/alloy-react-dropzone';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';

import AttachmentsDropzoneContextualMenu from './AttachmentsDropzoneContextualMenu';
import AttachmentsDropzoneDescription from './AttachmentsDropzoneDescription';
import { TAttachmentsContext } from '../../context/AttachmentsContext';
import { useDropzoneTitle } from '../../hooks/useDropzoneTitle';
import { useTranslation } from '../../i18n';

export type AttachmentsDropzoneProps = ComponentProps<typeof Dropzone> & {
  hideIllustration?: boolean;
  openProjectFilesModal?: TAttachmentsContext['openProjectFilesModal'];
  customDropzoneTitle?: string;
  isExceedsMaxFiles?: boolean;
  maxNumberOfAttachments?: number;
  /**
   * Whether users are allowed to delete attachments.
   * When true, displays additional guidance text in the max attachments warning banner.
   */
  allowDelete?: boolean;
  /**
   * Whether the attachments are in read-only mode.
   * When true, hides the max attachments warning banner.
   */
  readOnly?: boolean;
  enableUploadOnPaste?: boolean;
};

const AttachmentsDropzone: FC<AttachmentsDropzoneProps> = ({
  size = Dropzone.SIZES.SMALL,
  maxSize,
  onDropAccepted,
  onDropRejected,
  renderContent,
  hideIllustration,
  accept,
  openProjectFilesModal,
  customDropzoneTitle,
  isExceedsMaxFiles,
  maxNumberOfAttachments,
  disabled,
  allowDelete,
  readOnly,
  enableUploadOnPaste,
  ...props
}) => {
  const { t } = useTranslation();
  const getContentProps = useCallback(
    (open: () => void) => ({
      open: openProjectFilesModal ? () => {} : open,
      linkText: openProjectFilesModal ? (
        <AttachmentsDropzoneContextualMenu
          size={size}
          onFromLocalFilesClick={open}
          onFromProjectFilesClick={openProjectFilesModal}
          disabled={disabled}
        />
      ) : undefined,
    }),
    [size, openProjectFilesModal, disabled],
  );

  const dropzoneTitle = useDropzoneTitle({
    customDropzoneTitle,
    size,
    enableUploadOnPaste,
  });

  const renderDefaultContent = useCallback(
    (open: () => void) => {
      if (size === Dropzone.SIZES.SMALL) {
        return (
          <Dropzone.ContentSmall
            style={{ textAlign: 'center' }}
            {...getContentProps(open)}
            title={dropzoneTitle}
            data-testid="AttachmentsDropzoneDefaultContent"
          />
        );
      }

      return (
        <Dropzone.Content
          {...getContentProps(open)}
          style={{ padding: 0 }}
          hideIllustration={hideIllustration}
          title={dropzoneTitle}
          description={
            <AttachmentsDropzoneDescription
              maxNumberOfAttachments={maxNumberOfAttachments}
            />
          }
          data-testid="AttachmentsDropzoneDefaultContent"
        />
      );
    },
    [
      size,
      getContentProps,
      hideIllustration,
      maxNumberOfAttachments,
      dropzoneTitle,
    ],
  );

  return (
    <>
      {isExceedsMaxFiles && !readOnly && (
        <Banner
          inline={true}
          variant={Banner.VARIANTS.WARNING}
          style={{ marginBottom: theme.spacing.xSmall }}
          message={
            <>
              <Text typography="bodyMediumBold" style={{ display: 'block' }}>
                {t('MAX_ATTACHMENT_REACHED', { maxNumberOfAttachments })}
              </Text>
              {allowDelete && (
                <Text typography="bodyMedium">
                  {t('REMOVE_ATTACHMENTS_TO_ADD_MORE')}
                </Text>
              )}
            </>
          }
        />
      )}
      <Flex
        flexDirection="column"
        padding={theme.spacing.medium}
        backgroundColor="white"
      >
        <Dropzone
          size={size}
          onDropAccepted={onDropAccepted}
          onDropRejected={onDropRejected}
          maxSize={maxSize}
          accept={accept}
          renderContent={renderContent || renderDefaultContent}
          title={customDropzoneTitle || t('DROPZONE_TEXT')}
          disabled={disabled}
          {...props}
        />
        {size === Dropzone.SIZES.SMALL && (
          <AttachmentsDropzoneDescription
            maxNumberOfAttachments={maxNumberOfAttachments}
          />
        )}
      </Flex>
    </>
  );
};

const SuspendedAttachmentsDropzone: FC<AttachmentsDropzoneProps> =
  WithAlloySuspense(AttachmentsDropzone);
export default SuspendedAttachmentsDropzone;
